import { isValidElement, ReactElement, useMemo, useState } from 'react';
import { get } from 'lodash';
import {
  BaseChangeItem,
  ChangeItemAction,
  ChangeItemChangeset,
  ChangeItemChangesetWithItems,
  ChangesetItemType,
  DesignModuleLink
} from '../../../../types';
import { classSet, Icon, Table } from '@platform/ui-library';
import { dataType as TableData } from '@platform/ui-library/build/Table/types';
import { getDesignObjectLabel, isUUID, setMissingProperties } from '../../../../utilities';
import { NestedList } from '../../../../utilities/components';
import style from './style/change-log-item.module.scss';

const timeStampProps = ['lastChange', 'lastLocalChange', 'createdTime'];

type GenericChangeItem = BaseChangeItem & {
  items?: ChangeItemChangesetWithItems['items'];
  lastChange?: number;
  lastLocalChange?: number;
  link?: DesignModuleLink;
  node?: any;
  object?: any;
  prevConfig?: any;
};

type ChangeLogItemProps = {
  changeLabel: string;
  iconClass: string;
  item?: GenericChangeItem | null;
  name: string;
  openDetail: boolean;
};

type Value = string | number | boolean | null | undefined;

type Row = {
  key?: string[];
  previous?: Value | ReactElement;
  current?: Value | ReactElement;
  type?: ChangesetItemType | string;
};

const rowTypeLabels: Record<ChangesetItemType | ChangeItemAction, string> = {
  [ChangesetItemType.put]: 'Modified',
  [ChangesetItemType.del]: 'Deleted',
  [ChangesetItemType.overwrite]: 'Modified',
  [ChangeItemAction.add]: 'Added',
  [ChangeItemAction.delete]: 'Deleted',
  [ChangeItemAction.modify]: 'Modified'
};

export function ChangeLogItem({
  changeLabel,
  iconClass = '',
  item = null,
  name,
  openDetail = false
}: ChangeLogItemProps) {
  const [open, setOpen] = useState(openDetail);
  const rows = useMemo(() => (item ? getItemRows(item) : []), [item]);
  const hasDetail = !!rows.length;
  const sortedRows = setMissingProperties(rows).sort(({ key: key1 }, { key: key2 }) => {
    if (key1 && key2) {
      return key1.join('').localeCompare(key2.join(''));
    }
    return 0;
  });
  const tableData = rowsToTable(sortedRows);
  return (
    <div className={classSet([style.container, open && style.open])}>
      <div className={style.changeLogItem} onClick={() => setOpen(!open)}>
        {!!iconClass && <i className={'fe icon-' + iconClass} />}
        <span>{name}</span>
        <span className={style.reason}>{changeLabel}</span>
        {hasDetail && (
          <button type="button" className={style.toggleButton}>
            {open ? <Icon icon="cross" /> : <Icon icon="plus" />}
          </button>
        )}
      </div>
      {hasDetail && <Table data={tableData} theme={style} />}
    </div>
  );
}

function keysToLabels(keys: string[]): string[] {
  return keys.map((key, i) => {
    if (typeof key !== 'string') {
      return '';
    }
    if (isUUID(key)) {
      return key;
    }
    return getDesignObjectLabel(key, undefined, i === 0);
  });
}

function rowsToTable(rows: Row[]): TableData {
  return rows.map((row) => {
    const { key, previous, current } = row;
    const keyPartial = key ? (key?.length > 2 ? key.slice(2) : [key[key.length - 1]]) ?? [] : [];
    const keyString = keyPartial.join('.');
    const keyParts = 'key' in row ? keysToLabels(keyPartial) : [];
    return {
      ...(!keyParts.length
        ? {}
        : {
          Label: (
            <div className={style.keys}>
              {keyParts.map((k, i) => (
                <span key={i}>{k}</span>
              ))}
            </div>
          )
        }),
      ...('type' in row ? { Action: rowTypeLabels[row.type as ChangesetItemType] } : {}),
      ...('previous' in row ? { Previous: parseValue(keyString, previous) } : {}),
      Current: parseValue(keyString, current)
    };
  });
}

function parseValue(key: string | null, value: any): Value | ReactElement {
  if (value === undefined) {
    return null;
  }
  if (value === null) {
    return 'null';
  }
  if (typeof value === 'object') {
    if (isValidElement(value)) {
      return value;
    }
    return <NestedList data={value} />;
  }
  if (value && key && timeStampProps.includes(key)) {
    return new Date(value).toLocaleString();
  }
  return value.toString();
}

function getItemRows({ changeset, ...item }: GenericChangeItem): Row[] {
  const itemObject = item?.object ?? item?.node ?? item?.link;
  const { prevConfig } = item;

  const getItemChangesetRows = (changesetItem: ChangeItemChangeset): Row[] => {
    const { type } = changesetItem;
    let rows: Row[] = [];
    switch (type) {
      case ChangesetItemType.put:
      case ChangesetItemType.del: {
        const configKeyPath = changesetItem.key?.slice(2);
        rows.push({
          key: changesetItem.key,
          previous: 'previousValue' in changesetItem ? changesetItem.previousValue : get(prevConfig, configKeyPath),
          current: 'value' in changesetItem ? changesetItem.value : get(itemObject, changesetItem.key),
          type
        });
        break;
      }
      case ChangesetItemType.overwrite: {
        rows.push({
          key: [ChangesetItemType.overwrite],
          previous: undefined,
          current: undefined,
          type
        });
        break;
      }
      default: {
        if (changesetItem.items) {
          const newRows: Row[] = Object.entries(changesetItem.items).flatMap(([childKey, child]) => {
            if (!child.item) {
              return [];
            }
            const { action, item } = child;
            const refName =
              (item?.id === childKey &&
                ((item as any)?.argumentName ?? (item as any)?.name ?? (item as any)?.varName)) ??
              childKey;
            const key = [...changesetItem.key, refName];
            const configKeyPath = key.slice(2);
            const previous = get(prevConfig, configKeyPath);
            const childItem = action === 'delete' ? undefined : item;
            const previousItem = action === 'delete' ? item : previous;
            return [
              {
                key,
                previous: <NestedList data={previousItem} className={style.list} />,
                current: <NestedList data={childItem} className={style.list} />,
                type: action
              }
            ];
          });

          rows = [...rows, ...newRows];
        }
      }
    }
    return rows;
  };

  if (!changeset) {
    return [
      {
        current: <textarea value={JSON.stringify(itemObject)} readOnly rows={6} />
      }
    ];
  }

  return changeset.map(getItemChangesetRows).flat();
}
